(() => {
  const CONSENT_COOKIE = 'ldj_consent';
  const CONSENT_DAYS = 180;
  const CONSENT_SET_EVENT = 'ldj:consent-set';

  const isLocalDevelopment =
    window.location.hostname === '127.0.0.1' ||
    window.location.hostname === 'localhost';

  const getCookie = (name) => {
    const match = document.cookie.match(
      new RegExp(`(?:^|; )${name}=([^;]*)`)
    );

    return match ? decodeURIComponent(match[1]) : null;
  };

  const getLocalConsent = () => {
    const value = getCookie(CONSENT_COOKIE);

    return value === 'granted' || value === 'denied'
      ? value
      : null;
  };

  const setLocalConsent = (choice) => {
    const expires = new Date(
      Date.now() + CONSENT_DAYS * 24 * 60 * 60 * 1000
    ).toUTCString();

    document.cookie =
      `${CONSENT_COOKIE}=${choice};` +
      `expires=${expires};` +
      `path=/;SameSite=Lax`;
  };

  const clearLocalConsent = () => {
    document.cookie =
      `${CONSENT_COOKIE}=;` +
      `expires=Thu, 01 Jan 1970 00:00:00 GMT;` +
      `path=/;SameSite=Lax`;
  };

  const updateGoogleConsent = (choice) => {
    if (typeof window.gtag !== 'function') return;

    const value = choice === 'granted' ? 'granted' : 'denied';

    window.gtag('consent', 'update', {
      ad_storage: value,
      ad_user_data: value,
      ad_personalization: value,
      analytics_storage: value,
      functionality_storage: value,
      personalization_storage: value,
      security_storage: 'granted'
    });
  };

  const notifyConsent = (choice) => {
    window.dataLayer = window.dataLayer || [];

    window.dataLayer.push({
      event: 'ldj_consent_update',
      ldj_consent: choice
    });

    window.dispatchEvent(
      new CustomEvent(CONSENT_SET_EVENT, {
        detail: { choice }
      })
    );
  };

  const finishChoice = (choice) => {
    setLocalConsent(choice);
    updateGoogleConsent(choice);
    notifyConsent(choice);
  };

  const loadShopifyPrivacy = (callback) => {
    if (window.Shopify?.customerPrivacy) {
      callback(null, window.Shopify.customerPrivacy);
      return;
    }

    if (!window.Shopify?.loadFeatures) {
      callback(new Error('Shopify Customer Privacy API unavailable'));
      return;
    }

    window.Shopify.loadFeatures(
      [
        {
          name: 'consent-tracking-api',
          version: '0.1'
        }
      ],
      (error) => {
        if (error) {
          callback(error);
          return;
        }

        callback(null, window.Shopify.customerPrivacy);
      }
    );
  };

  const init = () => {
    const banner = document.querySelector('[data-cookie-consent]');

    if (!banner) return;
    if (banner.dataset.initialized === 'true') return;

    banner.dataset.initialized = 'true';

    const acceptButton = banner.querySelector('[data-cookie-accept]');
    const rejectButton = banner.querySelector('[data-cookie-reject]');

    const show = () => {
      banner.classList.add('is-visible');
      banner.setAttribute('aria-hidden', 'false');
    };

    const hide = () => {
      banner.classList.remove('is-visible');
      banner.setAttribute('aria-hidden', 'true');
    };

    const choose = (choice) => {
      /*
       * Local theme development:
       * Shopify's consent endpoint cannot reliably be called through
       * the HTTP localhost proxy, so use the LDJ consent locally.
       */
      if (isLocalDevelopment) {
        finishChoice(choice);
        hide();
        return;
      }

      /*
       * Real Shopify storefront:
       * update Shopify Customer Privacy first.
       */
      loadShopifyPrivacy((error, customerPrivacy) => {
        if (error || !customerPrivacy) {
          console.error(
            '[LDJ] Shopify Customer Privacy API could not be loaded.',
            error
          );

          return;
        }

        const granted = choice === 'granted';

        customerPrivacy.setTrackingConsent(
          {
            analytics: granted,
            marketing: granted,
            preferences: granted
          },
          () => {
            finishChoice(choice);
            hide();
          }
        );
      });
    };

    acceptButton?.addEventListener('click', () => {
      choose('granted');
    });

    rejectButton?.addEventListener('click', () => {
      choose('denied');
    });

    document.addEventListener('click', (event) => {
      const settingsButton = event.target.closest(
        '[data-cookie-settings-open]'
      );

      if (!settingsButton) return;

      clearLocalConsent();
      show();
    });

    /*
     * Local development uses only ldj_consent.
     */
    if (isLocalDevelopment) {
      if (!getLocalConsent()) {
        show();
      }

      return;
    }

    /*
     * Production/storefront reads the actual Shopify consent.
     */
    loadShopifyPrivacy((error, customerPrivacy) => {
      if (error || !customerPrivacy) {
        console.warn(
          '[LDJ] Customer Privacy API unavailable during initialization.'
        );

        if (!getLocalConsent()) {
          show();
        }

        return;
      }

      const consent = customerPrivacy.currentVisitorConsent();

      const hasShopifyChoice =
        consent.analytics === 'yes' ||
        consent.analytics === 'no' ||
        consent.marketing === 'yes' ||
        consent.marketing === 'no' ||
        consent.preferences === 'yes' ||
        consent.preferences === 'no';

      if (!hasShopifyChoice) {
        clearLocalConsent();
        show();
        return;
      }

      const granted =
        consent.analytics === 'yes' &&
        consent.marketing === 'yes' &&
        consent.preferences === 'yes';

      const choice = granted ? 'granted' : 'denied';

      setLocalConsent(choice);
      updateGoogleConsent(choice);
      hide();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
